/**
 * Authentication Context - OIDC Only
 *
 * Manages user authentication state globally using identity_service OIDC.
 * All authentication is handled through the OIDC Authorization Code Flow with PKCE.
 *
 * Architecture:
 * - identity_service (IdP) stores only identity + authentication data
 * - user_profiles_service stores app-specific data
 * - Frontend uses OIDC SDK for authentication flow
 */

import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import { jwtDecode } from 'jwt-decode';
import { configureTokenAccessor } from './axiosInterceptor';
import { configureSessionExpiredHandler } from './axiosClient';

// Import from shared SDK
import { OIDCClient } from 'onecall-auth-sdk';

// Import singleton instances
import { tokenManager, authSync } from './singletons';

// Return-URL persistence (single owner)
import { saveReturnUrl, isAuthPath } from './returnUrl';

// Cross-tab mutual exclusion around refresh
import { withRefreshLock } from './refreshLock';

// Re-export singletons for consumers
export { tokenManager, authSync } from './singletons';

const AuthContext = createContext(null);

// Module-level OIDC client singleton
let oidcClient = null;

// Two refresh failures this close together mean the session is dead, not the network.
const RENEW_FAILURE_WINDOW_MS = 30_000;

/**
 * Map OIDC User object to app user format
 * @param {Object} oidcUser - User from OIDC SDK
 * @param {Object} appProfile - Profile from user_profiles_service { role, organization_id }
 *
 * Name and email come from the profile service first, and only fall back to the token
 * claims. The profile is the authoritative record and is already fetched on every boot,
 * so this costs nothing extra.
 *
 * It also has to be this way round now. The ID token's claims are only present on a fresh
 * login: a page reload re-mints from the refresh cookie, and that response carries no ID
 * token. While the tokens were kept in web storage the old claims were restored on reload
 * and the gap never showed; once they stopped being persisted the user menu started
 * rendering a blank name and email with the role still filled in - the tell that the role
 * came from the profile and the rest did not.
 *
 * Pure, so it lives outside the component: nothing about it changes between renders.
 */
const mapOidcUserToAppUser = (oidcUser, appProfile = { role: 'user', organization_id: null }) => {
  const cachedName = appProfile.cached_name || '';
  const [cachedFirst = '', ...cachedRest] = cachedName.split(' ');

  return {
    id: oidcUser.sub,
    email: appProfile.cached_email || oidcUser.email,
    first_name: cachedFirst || oidcUser.given_name || '',
    last_name: cachedRest.join(' ') || oidcUser.family_name || '',
    name: cachedName
      || oidcUser.name
      || `${oidcUser.given_name || ''} ${oidcUser.family_name || ''}`.trim(),
    mobile: oidcUser.phone_number || oidcUser.mobile || '',
    picture: oidcUser.picture,
    email_verified: oidcUser.email_verified,
    roles: appProfile.cached_roles || [],
    organization_id: appProfile.organization_id,
    role: appProfile.role,
    created_at: appProfile.created_at,
    is_active: appProfile.is_active,
  };
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children, apiConfig, defaultReturnUrl = '/dashboard' }) => {
  // State
  const [accessToken, setAccessToken] = useState(null);
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Organization data derived from token
  const [organizationId, setOrganizationId] = useState(null);
  const [organizationRole, setOrganizationRole] = useState(null);

  // Ref to track initialization
  const initializedRef = useRef(false);

  // When the last silent refresh failed; see the silentRenewError binding
  const lastRenewFailureAtRef = useRef(0);

  // Store apiConfig in a ref for closure stability
  const apiConfigRef = useRef(apiConfig);
  useEffect(() => {
    apiConfigRef.current = apiConfig;
  }, [apiConfig]);

  const getOidcClient = () => {
    if (!oidcClient) {
      const config = apiConfigRef.current.getOidcConfig();
      oidcClient = new OIDCClient({
        ...config,
        // With a session endpoint the refresh token is an HttpOnly cookie, so nothing worth
        // stealing needs to persist: keep the access and id tokens in memory only, and no
        // auth token is written to web storage at all. Apps without the endpoint keep
        // sessionStorage, because memory alone would log them out on every reload.
        storage: config.sessionEndpoint ? 'memory' : 'sessionStorage',
        // TokenManager owns refresh. The SDK's own scheduler fired at the same instant with
        // the same buffer, so every cycle sent two refreshes carrying one rotating cookie and
        // the second was rejected. One scheduler, one mutex, one cross-tab lock.
        automaticSilentRenew: false,
      });
    }
    return oidcClient;
  };

  /**
   * Extract organization data from JWT token
   */
  const extractOrganizationData = useCallback((token) => {
    try {
      const decoded = jwtDecode(token);
      const orgId = decoded.organization_id || decoded.org_id;
      const orgRole = decoded.organization_role;

      if (orgId !== undefined) {
        setOrganizationId(orgId);
      }
      if (orgRole) {
        setOrganizationRole(orgRole);
      }
    } catch (error) {
      // Token decode failed - will be handled by auth flow
    }
  }, []);

  /**
   * Set organization role from profile data
   * Called after fetching profile from user_profiles_service
   */
  const setOrganizationRoleFromProfile = useCallback((role) => {
    if (role) {
      setOrganizationRole(role);
    }
  }, []);

  /**
   * Clear all authentication data from memory
   */
  const clearAuthData = useCallback(() => {
    setUser(null);
    setAccessToken(null);
    setIsAuthenticated(false);
    setOrganizationId(null);
    setOrganizationRole(null);
    // Clear TokenManager
    tokenManager.clear();
  }, []);

  /**
   * Handle session expiration (called by axios interceptor on auth failure)
   * Saves current URL and redirects to login
   */
  const handleSessionExpired = useCallback(() => {
    // Save current URL for redirect after login (validated + auth-loop-guarded in the util)
    saveReturnUrl(window.location.pathname + window.location.search);

    // Clear auth state
    clearAuthData();

    // Notify user. Fired before the navigation below on purpose: when the SDK has already
    // unloaded the user, ProtectedRoute has soft-navigated to /auth by now and the Toaster
    // (mounted outside Routes) is still up, so this is seen. A hard navigation destroys it.
    toast.error('Your session has expired. Please log in again.');

    // Redirect to login - unless already there. Assigning the same path reloads the document,
    // and a reload boots the provider, which refreshes, which can fail, which lands back here.
    if (!isAuthPath(window.location.pathname)) {
      window.location.href = '/auth';
    }
  }, [clearAuthData]);

  /**
   * Fetch app-specific profile from user_profiles_service
   * Returns both role and organization_id
   */
  const fetchAppProfile = useCallback(async (accessToken) => {
    try {
      const response = await fetch(
        `${apiConfigRef.current.getApiGatewayUrl()}/profile/me`,
        { headers: { 'Authorization': `Bearer ${accessToken}` } }
      );
      if (response.ok) {
        const profile = await response.json();
        console.log('[AuthContext] Profile fetched:', {
          role: profile.role,
          organization_id: profile.organization_id
        });
        return {
          role: profile.role || 'user',
          organization_id: profile.organization_id ?? null,
          is_active: profile.is_active,
          created_at: profile.created_at,
          // Who the user is. The response carries these and this projection used to drop
          // them, so mapOidcUserToAppUser fell back to ID-token claims - which a reload does
          // not have, leaving the user menu blank. Named explicitly rather than spreading
          // `profile`, so nothing else in the response leaks into app state unnoticed.
          cached_name: profile.cached_name,
          cached_email: profile.cached_email,
          cached_roles: profile.cached_roles
        };
      }
      console.warn('[AuthContext] Failed to fetch profile, status:', response.status);
    } catch (error) {
      console.error('[AuthContext] Profile fetch error:', error);
    }
    return { role: 'user', organization_id: null };
  }, []);

  /**
   * Adopt a signed-in user into React state and arm the refresh timer.
   *
   * Four paths land here: boot (a stored or cookie-refreshed session), the OIDC callback,
   * the SDK's userLoaded event, and a login broadcast from another tab. Each used to carry
   * its own copy of this sequence and the copies drifted - three armed TokenManager and the
   * cross-tab one did not, which is how a tab could end up holding a token with no refresh
   * timer behind it. Only the trailing broadcast differs per path, so callers do that part
   * themselves rather than passing a mode in.
   *
   * Returns the token's remaining lifetime in seconds, because the callers that broadcast
   * need the same number and should not compute it twice.
   */
  const adoptSession = useCallback(async (signedIn) => {
    setAccessToken(signedIn.access_token);
    const appProfile = await fetchAppProfile(signedIn.access_token);
    setUser(mapOidcUserToAppUser(signedIn, appProfile));
    setIsAuthenticated(true);
    setOrganizationId(appProfile.organization_id);
    setOrganizationRoleFromProfile(appProfile.role);
    extractOrganizationData(signedIn.access_token);
    const expiresIn = signedIn.expires_at - Math.floor(Date.now() / 1000);
    tokenManager.setToken(signedIn.access_token, expiresIn);
    return expiresIn;
  }, [fetchAppProfile, extractOrganizationData, setOrganizationRoleFromProfile]);

  /**
   * Configure axios session expired handler
   */
  useEffect(() => {
    configureSessionExpiredHandler(handleSessionExpired);
  }, [handleSessionExpired]);

  /**
   * Initialize AuthSync for cross-tab communication
   */
  useEffect(() => {
    authSync.initialize();

    // Setup cross-tab sync handlers
    authSync.setHandlers({
      onLogout: () => {
        // Another tab logged out - clear local state silently
        console.log('[AuthContext] Received logout from another tab');
        clearAuthData();
        // Redirect to login without toast (user initiated logout in other tab)
        window.location.href = '/auth';
      },
      onTokenRefresh: (token, expiresIn) => {
        // Another tab refreshed the token. Adopt it AND re-arm TokenManager off its expiry, or
        // this tab keeps its old timer, fires on its own schedule, and queues on the refresh
        // lock for a rotation it does not need. A missing expiry must not arm a NaN timer.
        console.log('[AuthContext] Received token refresh from another tab');
        setAccessToken(token);
        extractOrganizationData(token);
        if (Number.isFinite(expiresIn) && expiresIn > 0) {
          tokenManager.setToken(token, expiresIn);
        }
      },
      onLogin: async () => {
        // Another tab logged in - adopt its session here too, refresh timer included
        console.log('[AuthContext] Received login from another tab');
        const client = getOidcClient();
        const oidcUser = client.getUser();
        if (oidcUser) {
          await adoptSession(oidcUser);
        }
      }
    });

    return () => {
      authSync.destroy();
    };
  }, [extractOrganizationData, clearAuthData, adoptSession]);

  /**
   * Configure axios interceptor to use current token
   */
  useEffect(() => {
    configureTokenAccessor(
      () => accessToken,
      (token) => {
        setAccessToken(token);
        if (token) {
          extractOrganizationData(token);
        }
      }
    );
  }, [accessToken, extractOrganizationData]);

  /**
   * Configure TokenManager to use OIDC refresh.
   *
   * Runs under the cross-tab lock: every tab shares one rotating refresh cookie, and two tabs
   * refreshing at once was the collision that reloaded the page. While this tab waited for the
   * lock another tab may have refreshed and broadcast; its token arrives via onTokenRefresh,
   * which re-arms TokenManager and moves the expiry. So: snapshot the expiry before waiting,
   * and if it moved and the token is valid, use that token and skip the network - the way
   * auth0-spa-js re-checks its cache after acquiring. "Changed" is the test, not "valid":
   * the axios 401 path arrives here holding a token that is valid locally and rejected by the
   * server, and that one must be refreshed, not handed back.
   *
   * State adoption and the broadcast are NOT done here. The SDK emits userLoaded on every
   * successful refresh and that handler does both, once.
   */
  useEffect(() => {
    tokenManager.setRefreshFunction(() => {
      const expiryBeforeWaiting = tokenManager.getState().expiresAt;
      return withRefreshLock(async () => {
        const current = tokenManager.getState();
        if (current.isValid && current.expiresAt !== expiryBeforeWaiting) {
          console.log('[AuthContext] Another tab refreshed while waiting; using its token');
          return current.token;
        }
        try {
          const client = getOidcClient();
          const newUser = await client.refreshToken();
          if (newUser && newUser.access_token) {
            // Arm TokenManager the instant the token exists. userLoaded's adoptSession arms it
            // again after the profile fetch with the same values, which is harmless.
            const expiresIn = newUser.expires_at - Math.floor(Date.now() / 1000);
            tokenManager.setToken(newUser.access_token, expiresIn);
            lastRenewFailureAtRef.current = 0;
            return newUser.access_token;
          }
          return null;
        } catch (error) {
          console.error('[AuthContext] OIDC refresh failed:', error);
          return null;
        }
      });
    });
  }, []);

  /**
   * Initialize authentication state from OIDC client
   */
  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    const initializeAuth = async () => {
      try {
        console.log('[AuthContext] Initializing authentication');
        const client = getOidcClient();

        // Check if we have a stored user session
        const oidcUser = client.getUser();
        const { sessionEndpoint } = apiConfigRef.current.getOidcConfig();

        if (oidcUser && client.isAuthenticated()) {
          console.log('[AuthContext] Found valid session');
          await adoptSession(oidcUser);
        } else if ((oidcUser && oidcUser.refresh_token) || sessionEndpoint) {
          // Either the access token expired and a refresh token is readable, or we are in
          // cookie mode - where nothing is stored at all, so a reload always lands here and
          // the only way back in is to ask the server, which still holds the cookie. Gating
          // this on `oidcUser.refresh_token` alone would log every cookie-mode user out on
          // every refresh of the page.
          console.log('[AuthContext] No usable token in memory, asking the server');
          // Under the cross-tab lock too: a tab reloading while another tab is mid-refresh is
          // the same race as two timers. Boot always refreshes rather than re-checking, since
          // it needs the user object and a broadcast carries only the token.
          const newUser = await withRefreshLock(() => client.refreshToken());
          if (newUser) {
            await adoptSession(newUser);
          }
        }

        // Set up OIDC event handlers
        client.on('userLoaded', async (oidcUser) => {
          console.log('[AuthContext] User loaded event');
          const expiresIn = await adoptSession(oidcUser);
          authSync.broadcastTokenRefresh(oidcUser.access_token, expiresIn);
        });

        client.on('accessTokenExpiring', () => {
          console.log('[AuthContext] Token expiring soon');
        });

        // accessTokenExpired is deliberately NOT bound. The SDK emits it from getUser() as a
        // side effect of a read, and isAuthenticated() reads twice; wiring it to a navigation
        // meant that merely checking auth state after expiry reloaded the page.

        client.on('silentRenewError', (_user, error) => {
          // A refresh failed. TokenManager retries on its own (backoff 1s/2s/4s), so the first
          // failure is left to it: a dead session fails again within seconds, a network blip
          // does not. Only a second failure inside the window ends the session. Nothing is
          // retried from here - the refresh lock is held while this fires and is not
          // reentrant, so a nested refresh would wait on itself.
          console.error('[AuthContext] Silent renew error:', error);
          const now = Date.now();
          if (now - lastRenewFailureAtRef.current < RENEW_FAILURE_WINDOW_MS) {
            lastRenewFailureAtRef.current = 0;
            handleSessionExpired();
            return;
          }
          lastRenewFailureAtRef.current = now;
        });

        client.on('userUnloaded', () => {
          console.log('[AuthContext] User unloaded');
          clearAuthData();
        });
      } catch (error) {
        console.error('[AuthContext] Initialization error:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, [handleSessionExpired, clearAuthData, adoptSession]);

  /**
   * Start OIDC login flow
   * Redirects to identity_service for authentication
   */
  const login = useCallback((returnUrl, loginHint) => {
    // Persist where to land after login (validated + auth-loop-guarded in the util)
    saveReturnUrl(returnUrl || window.location.pathname + window.location.search);

    // Start OIDC login flow
    const client = getOidcClient();
    client.login({
      loginHint: loginHint || undefined,
    });
  }, []);

  /**
   * Handle OIDC callback after redirect from IdP
   * Called by OIDCCallback page
   * Note: code and state are read from URL by SDK's handleCallback()
   */
  const handleOIDCCallback = useCallback(async () => {
    try {
      const client = getOidcClient();

      // The SDK handles code exchange internally via handleCallback()
      const oidcUser = await client.handleCallback();

      await adoptSession(oidcUser);

      // Notify other tabs
      authSync.broadcastLogin({ userId: oidcUser.sub });

      toast.success('Login successful!');
      return { success: true };
    } catch (error) {
      console.error('[AuthContext] OIDC callback error:', error);
      return { success: false, error: error.message };
    }
  }, [adoptSession]);

  /**
   * Logout user
   * Redirects to identity_service for OIDC logout
   */
  const logoutUser = useCallback(async () => {
    try {
      const client = getOidcClient();

      // Clear local state first. This must NOT grow to remove the SDK's own `onecall_auth:*`
      // keys: client.logout() below reads the stored user to decide whether to revoke, so
      // clearing them here would silently turn logout into a local-only redirect and leave
      // both tokens live on the server.
      clearAuthData();

      // Notify other tabs about logout
      authSync.broadcastLogout();

      toast.success('Logged out successfully');

      // Redirect to IdP logout (this will redirect back to our app)
      await client.logout();
    } catch (error) {
      console.error('[AuthContext] Logout error:', error);
      // Even on error, clear local state and redirect
      clearAuthData();
      window.location.href = '/auth';
    }
  }, [clearAuthData]);

  /**
   * Clear local session and start a new login flow without full OIDC logout redirect.
   *
   * Unlike logoutUser() which redirects to the IdP's end_session endpoint (and then
   * the root URL), this clears local state in-place and immediately starts a new
   * login flow. Needed for account-switching where we must return to a specific URL
   * (e.g., invitation acceptance) after re-authentication.
   *
   * @param {string} returnUrl - URL to redirect to after successful re-login
   * @param {string} [loginHint] - Email to pre-fill on the login form
   */
  const clearSessionAndLogin = useCallback(async (returnUrl, loginHint) => {
    // Best-effort: revoke refresh token on server
    try {
      const client = getOidcClient();
      await client.revokeToken();
    } catch (error) {
      console.error('[AuthContext] Token revocation failed during session switch:', error);
    }

    // Clear React auth state and TokenManager
    clearAuthData();

    // Clear OIDC SDK storage to prevent stale PKCE errors
    sessionStorage.removeItem('onecall_auth:user');
    sessionStorage.removeItem('onecall_auth:code_verifier');
    sessionStorage.removeItem('onecall_auth:state');
    sessionStorage.removeItem('onecall_auth:nonce');

    // Set return URL for post-login redirect
    saveReturnUrl(returnUrl);

    // Start new login flow with forced re-authentication
    const freshClient = getOidcClient();
    freshClient.login({
      prompt: 'login',
      loginHint: loginHint || undefined,
    });
  }, [clearAuthData]);

  /**
   * Update user profile (via user_profiles_service)
   */
  const updateUserProfile = useCallback(async (profileData) => {
    try {
      if (!accessToken) {
        return { success: false, error: 'Not authenticated' };
      }

      const response = await fetch(`${apiConfigRef.current.IDENTITY_SERVICE_URL}/oauth/profile`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify(profileData)
      });

      if (response.ok) {
        const data = await response.json();
        setUser(prev => ({ ...prev, ...data }));
        toast.success('Profile updated successfully');
        return { success: true, data };
      } else {
        const errorData = await response.json().catch(() => ({}));
        return { success: false, error: errorData.detail || 'Update failed' };
      }
    } catch (error) {
      return { success: false, error: 'Failed to update profile' };
    }
  }, [accessToken]);

  /**
   * Change the current user's password (via identity /oauth/change-password).
   * On success the server revokes sessions, so the caller should log out.
   */
  const changeUserPassword = useCallback(async (currentPassword, newPassword) => {
    try {
      if (!accessToken) {
        return { success: false, error: 'Not authenticated' };
      }

      const response = await fetch(`${apiConfigRef.current.IDENTITY_SERVICE_URL}/oauth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({ current_password: currentPassword, new_password: newPassword })
      });

      if (response.ok) {
        toast.success('Password changed successfully');
        return { success: true };
      }
      const errorData = await response.json().catch(() => ({}));
      return { success: false, error: errorData.detail || 'Failed to change password' };
    } catch (error) {
      return { success: false, error: 'Failed to change password' };
    }
  }, [accessToken]);

  const value = {
    // State
    user,
    accessToken,
    isAuthenticated,
    isLoading,
    organizationId,
    organizationRole,

    // OIDC Methods
    login,
    handleOIDCCallback,
    logoutUser,

    // Profile Methods
    updateUserProfile,
    changeUserPassword,
    clearAuthData,
    clearSessionAndLogin,

    // Config (for PermissionContext and other consumers)
    apiConfig: apiConfigRef.current,
    defaultReturnUrl,

    // Legacy compatibility (deprecated - these now redirect to OIDC)
    loginWithEmail: () => login(),
    loginWithGoogle: () => login(),
    registerWithEmail: () => { window.location.href = `${apiConfigRef.current.getIdentityServiceUrl()}/register`; },
    forgotPassword: () => { window.location.href = `${apiConfigRef.current.getIdentityServiceUrl()}/forgot-password`; },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
